import { getSession } from '../auth';
import { toast } from '../components/toast';
import { createStudent, deleteStudentRecord, isProductionData, listStudents, updateStudentRecord } from '../lib/repository';
import { openModal, closeModal } from '../components/modal';
import type { StudentRecord } from '../domain';

let roster: StudentRecord[] = [];
let loading = false;
let loaded = false;
let query = '';

function load(): void {
  if (loading || loaded || !isProductionData()) return;
  loading = true;
  listStudents().then((r) => { roster = r; loaded = true; loading = false; (window as any).__rerender?.(); })
    .catch((err) => { toast(err instanceof Error ? err.message : 'Could not load students', 'warn'); loading = false; roster = []; });
}

const AV_COLORS = ['av-c1', 'av-c2', 'av-c3', 'av-c4', 'av-c5', 'av-c6', 'av-c7'];
function avClass(name: string): string {
  const sum = name.split('').reduce((a, ch) => a + ch.charCodeAt(0), 0);
  return AV_COLORS[sum % AV_COLORS.length];
}

export function renderStudents(): string {
  load();
  const session = getSession();
  const canManage = !!session && ['owner', 'admin', 'teamLead'].includes(session.role) && isProductionData();
  const q = query.toLowerCase();
  const filtered = roster.filter((s) => !q || s.name.toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q) || (s.phone || '').toLowerCase().includes(q));
  const rows = filtered.map((s) => {
    const initials = (s.name || '?').slice(0, 2).toUpperCase();
    const statusPill = s.status === 'active'
      ? '<span class="pill pill-success"><span class="dot live"></span> active</span>'
      : '<span class="pill pill-info">' + (s.status || 'active') + '</span>';
    return '<div class="card card-pad-sm sk-student-card" data-student-id="' + s.id + '" data-student-row>' +
      '<div class="avatar ' + avClass(s.name || '?') + '">' + initials + '</div>' +
      '<div class="sk-student-main">' +
        '<div class="sk-student-name">' + escapeHtml(s.name) + '</div>' +
        '<div class="sk-student-meta">' + escapeHtml(s.email || '—') + (s.phone ? ' · ' + escapeHtml(s.phone) : '') + '</div>' +
      '</div>' +
      statusPill +
      (canManage ? '<div class="sk-student-actions">' +
        '<button type="button" class="icon-btn" data-student-edit="' + s.id + '" title="Edit student" aria-label="Edit student">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>' +
        '</button>' +
        '<button type="button" class="icon-btn icon-btn-danger" data-student-del="' + s.id + '" title="Remove student" aria-label="Remove student">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>' +
        '</button>' +
      '</div>' : '') +
      '<div style="display:none" data-student-hidden="' + s.id + '" data-student-name="' + escapeHtml(s.name || '') + '" data-student-email="' + escapeHtml(s.email || '') + '" data-student-phone="' + escapeHtml(s.phone || '') + '" data-student-status="' + escapeHtml(s.status || 'active') + '"></div>' +
      '</div>';
  }).join('');
  return (
    '<div class="screen-head"><div><div class="t-eyebrow">Students</div><h1 class="t-h1">Students</h1>' +
    '<p class="t-meta">' + (loading ? 'loading…' : roster.length + ' learners') + '</p></div>' +
    (canManage ? '<button type="button" class="btn btn-accent touch-target" data-action="student-add">+ Add student</button>' : '') + '</div>' +
    '<div style="margin:12px 0"><input class="form-input" data-search placeholder="Search name, email or phone…" value="' + escapeHtml(query) + '" style="max-width:380px"/></div>' +
    '<div class="sk-student-list">' + (rows || '<div class="card card-pad-lg empty"><div class="empty-illus">🎒</div><h2>No students yet</h2><p>Add your first learner, or convert a lead from the Pipeline.</p></div>') + '</div>'
  );
}

export function bindStudents(): void {
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-action="student-add"]')) { openAddForm(); return; }
    if (t.closest('[data-student-close]')) { closeModal('student-add'); closeModal('student-edit'); closeModal('student-delete'); return; }
    const edit = t.closest<HTMLElement>('[data-student-edit]');
    if (edit && edit.dataset.studentEdit) {
      const row = edit.closest<HTMLElement>('[data-student-row]');
      const hidden = row?.querySelector<HTMLElement>('[data-student-hidden]');
      if (!row || !hidden) return;
      openEditForm(edit.dataset.studentEdit, {
        name: hidden.dataset.studentName || '',
        email: hidden.dataset.studentEmail || '',
        phone: hidden.dataset.studentPhone || '',
        status: hidden.dataset.studentStatus || 'active',
      });
      return;
    }
    const del = t.closest<HTMLElement>('[data-student-del]');
    if (del && del.dataset.studentDel) {
      const row = del.closest<HTMLElement>('[data-student-row]');
      const hidden = row?.querySelector<HTMLElement>('[data-student-hidden]');
      const name = hidden?.dataset.studentName || 'this student';
      openDeleteConfirm(del.dataset.studentDel, name);
      return;
    }
  });
  document.addEventListener('input', (e) => {
    const q = (e.target as HTMLElement).closest<HTMLInputElement>('[data-search]');
    if (q) { query = q.value; (window as any).__rerender?.(); }
  });
  document.addEventListener('submit', async (e) => {
    const form = (e.target as HTMLElement).closest<HTMLFormElement>('#student-add-form, #student-edit-form');
    if (!form) return;
    e.preventDefault();
    const isEdit = form.id === 'student-edit-form';
    const fd = new FormData(form);
    const name = String(fd.get('name') || '').trim();
    const email = String(fd.get('email') || '').trim();
    const phone = String(fd.get('phone') || '').trim();
    const status = String(fd.get('status') || 'active') as 'active' | 'paused' | 'completed' | 'withdrawn';
    if (!name) { toast('Name is required', 'warn'); return; }
    const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]'); if (btn) btn.disabled = true;
    try {
      if (isEdit) {
        const id = form.dataset.editId || '';
        if (isProductionData()) await updateStudentRecord(id, { name, email, phone, status });
        const i = roster.findIndex((s) => s.id === id);
        if (i > -1) roster[i] = { ...roster[i], name, email, phone, status };
        toast('Student updated', 'success');
        closeModal('student-edit');
      } else {
        if (isProductionData()) await createStudent({ name, email, phone, course: String(fd.get('course') || '') });
        toast('Student added', 'success');
        closeModal('student-add');
        loaded = false;
      }
      (window as any).__rerender?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not save', 'warn');
      if (btn) btn.disabled = false;
    }
  });
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const confirm = t.closest<HTMLElement>('[data-confirm-student-delete]');
    if (confirm) {
      const id = confirm.dataset.confirmStudentDelete;
      const btns = document.querySelectorAll<HTMLButtonElement>('[data-confirm-student-delete]');
      btns.forEach((b) => b.disabled = true);
      void deleteStudentRecord(id)
        .then(() => { toast('Student removed', 'success'); roster = roster.filter((s) => s.id !== id); closeModal('student-delete'); (window as any).__rerender?.(); })
        .catch((err) => { btns.forEach((b) => b.disabled = false); toast(err instanceof Error ? err.message : 'Could not remove', 'warn'); });
    }
  });
}

function initialFor(name: string): string {
  return (name || '?').slice(0, 2).toUpperCase();
}

function openAddForm(): void {
  openModal({
    id: 'student-add',
    title: 'Add student',
    subtitle: 'Creates a learner record in this workspace.',
    width: 520,
    html: '<form id="student-add-form">' +
      '<div class="sk-form-head"><div class="avatar avatar-lg av-c3">+</div><div class="sk-form-head-txt"><div class="t-h3">New learner</div><div class="t-meta">Their details appear instantly in attendance and batch rosters.</div></div></div>' +
      '<div class="sk-field"><label class="t-eyebrow">Full name <span class="req">*</span></label><input required name="name" placeholder="e.g. Anjali Sharma" class="form-input sk-input-lg"/></div>' +
      '<div class="sk-grid-2">' +
        '<div class="sk-field"><label class="t-eyebrow">Email</label><input type="email" name="email" placeholder="anjali@example.com" class="form-input"/></div>' +
        '<div class="sk-field"><label class="t-eyebrow">Phone</label><input name="phone" placeholder="+91 98XXX XXXXX" class="form-input"/></div>' +
      '</div>' +
      '<div class="sk-field"><label class="t-eyebrow">Course (tag)</label><input name="course" placeholder="e.g. Entrepreneurship Incubator" class="form-input"/></div>' +
      '<div class="sk-footer"><button type="button" class="btn btn-ghost" data-student-close>Cancel</button>' +
      '<button type="submit" class="btn btn-primary">Add student</button></div>' +
    '</form>',
  });
}

function openEditForm(id: string, current: { name: string; email: string; phone: string; status: string }): void {
  const opts = [
    ['active', 'Active'],
    ['paused', 'Paused'],
    ['completed', 'Completed'],
    ['withdrawn', 'Withdrawn'],
  ];
  const pills = opts.map(([val, label]) => '<button type="button" class="sk-seg ' + (current.status === val ? 'on' : '') + '" data-sk-status="' + val + '">' + label + '</button>').join('');
  openModal({
    id: 'student-edit',
    title: 'Edit student',
    subtitle: 'Update learner details — changes save instantly.',
    width: 540,
    html: '<form id="student-edit-form" data-edit-id="' + id + '">' +
      '<div class="sk-form-head"><div class="avatar avatar-lg ' + avClass(current.name || '?') + '">' + initialFor(current.name) + '</div>' +
      '<div class="sk-form-head-txt"><div id="sk-edit-name-live" class="t-h3">' + escapeHtml(current.name) + '</div><div class="t-meta">' + escapeHtml(current.email || 'no email') + '</div></div></div>' +
      '<div class="sk-field"><label class="t-eyebrow">Full name</label><input required name="name" data-sk-live value="' + escapeHtml(current.name) + '" class="form-input sk-input-lg"/></div>' +
      '<div class="sk-grid-2">' +
        '<div class="sk-field"><label class="t-eyebrow">Email</label><input type="email" name="email" value="' + escapeHtml(current.email) + '" class="form-input"/></div>' +
        '<div class="sk-field"><label class="t-eyebrow">Phone</label><input name="phone" value="' + escapeHtml(current.phone) + '" class="form-input"/></div>' +
      '</div>' +
      '<input type="hidden" name="status" value="' + escapeHtml(current.status) + '"/>' +
      '<div class="sk-field"><label class="t-eyebrow">Status</label><div class="sk-seg-row" role="tablist">' + pills + '</div></div>' +
      '<div class="sk-footer"><button type="button" class="btn btn-ghost" data-student-close>Cancel</button>' +
      '<button type="submit" class="btn btn-primary">Save changes</button></div>' +
    '</form>',
    onMount: (panel) => {
      const nameInput = panel.querySelector<HTMLInputElement>('[data-sk-live]');
      const live = panel.querySelector<HTMLElement>('#sk-edit-name-live');
      if (nameInput && live) nameInput.addEventListener('input', () => { live.textContent = nameInput.value || '—'; });
      const segRow = panel.querySelector<HTMLElement>('.sk-seg-row');
      const statusInput = panel.querySelector<HTMLInputElement>('input[name="status"]');
      if (segRow && statusInput) {
        segRow.querySelectorAll<HTMLButtonElement>('[data-sk-status]').forEach((b) => b.addEventListener('click', () => {
          segRow.querySelectorAll('[data-sk-status]').forEach((x) => x.classList.remove('on'));
          b.classList.add('on');
          statusInput.value = b.dataset.skStatus || 'active';
        }));
      }
    },
  });
}

function openDeleteConfirm(id: string, name: string): void {
  openModal({
    id: 'student-delete',
    title: 'Remove ' + name + '?',
    subtitle: 'This permanently deletes the student record and detaches them from any batch.',
    width: 460,
    html: '<p class="t-meta" style="margin-bottom:20px">If you only want to pause them, use <strong>Edit</strong> and set Status to <strong>Paused</strong> instead.</p>' +
      '<div class="row" style="gap:10px;justify-content:flex-end">' +
      '<button type="button" class="btn btn-ghost" data-student-close>Cancel</button>' +
      '<button type="button" class="btn btn-danger" data-confirm-student-delete="' + id + '">Remove student</button>' +
      '</div>',
  });
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}